import { Component, EventEmitter, Input, Output, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { CotacaoService } from '../../../services/cotacao/cotacao.service';
import { CotacaoDialogService, CotacaoSalvaPayload } from '../../../services/cotacao/cotacao-dialog.service';
import { EmpresaConfigService } from '../../../services/configuracoes/empresa.service';
import { toLocalISOString } from '../../utils/date-utils';
import { MessageService } from 'primeng/api';
import { Moeda } from '../../../enums/moeda.enum';
import { CampoCotacao, camposCotacaoVisiveis } from '../../../utils/moeda-pareamento.util';

@Component({
  selector: 'app-cotacao-dialog',
  templateUrl: './cotacao-dialog.component.html',
  styleUrls: ['./cotacao-dialog.component.scss'],
  providers: [MessageService],
})
export class CotacaoDialogComponent implements OnInit, OnChanges, OnDestroy {
  @Input() visivel = false;
  @Input() empresaId: number | null = null;

  /**
   * Moeda padrao da empresa DONA da cotacao, informada pelo componente pai
   * quando ele ja a resolveu no contexto correto (opt-in).
   *
   * Mesmo padrao de `forma-pagamento-cards`/`forma-pagamento-dialog`: quem sabe
   * qual e a empresa certa e o pai; o componente compartilhado apenas recebe.
   *
   * Obrigatorio na Area do Contador ("Operar Cliente"): la o dialog opera sobre
   * a empresa do CLIENTE, mas `GET /configuracaoEmpresa/sessao` responde sempre
   * pela empresa do CONTADOR — a rota fica de fora do allowlist do
   * `ContadorContextoInterceptor` por ser global (menu/header) e a resposta
   * ainda e cacheada por sessao de usuario. Sem este input, o dialog exibiria os
   * pares da moeda do contador e gravaria `moedaPadrao` errado.
   *
   * Quando nao informado, mantem o comportamento historico (moeda da propria
   * empresa logada, via sessao).
   */
  @Input() moedaEmpresa?: Moeda | string | null;

  @Output() visivelChange = new EventEmitter<boolean>();
  /**
   * Chave i18n do subtitulo. Quando nao informada usa `cotacao_dialog.subtitulo`
   * ("cotacao desatualizada"). Ex.: `cotacao_dialog.subtitulo_nao_cadastrada`.
   */
  @Input() subtituloChave?: string | null;

  @Output() cotacaoSalva = new EventEmitter<CotacaoSalvaPayload>();
  /** Emitido quando o usuario fecha o dialogo SEM salvar a cotacao. */
  @Output() cancelado = new EventEmitter<void>();

  /**
   * Fallback hardcoded de ARS->PYG quando a busca externa (cambioschaco) falha
   * ou nao retorna ARS. Valor aproximado historico de cotacao 1 ARS ≈ 7,85 PYG
   * para permitir que o formulario funcione mesmo offline / fora do horario
   * comercial. Quando houver cotacao atualizada, ela sobrescreve este valor.
   */
  private static readonly PESO_GUARANI_FALLBACK = 7.85;

  dolarGuaraniInput = 0;
  realGuaraniInput = 0;
  dolarRealInput = 0;
  pesoGuaraniInput: number | null = null;
  cotacaoCarregando = false;

  /**
   * Moeda padrao efetivamente usada pelo dialog: vem do `@Input() moedaEmpresa`
   * quando o pai a informa, senao da sessao da propria empresa logada.
   * Determina quais pares aparecem e o `moedaPadrao` gravado no POST.
   */
  private moedaResolvida: Moeda | null = null;
  /** Campos de cotacao visiveis no dialog (derivados da moeda resolvida). */
  camposVisiveis: CampoCotacao[] = camposCotacaoVisiveis(null);

  /** Evita repetir o GET da sessao entre ngOnChanges e ngOnInit. */
  private sessaoConsultada = false;
  /** Moeda da sessao (resolvida uma vez); usada quando o pai deixa de informar `moedaEmpresa`. */
  private moedaSessao: Moeda | null = null;

  texts = {
    titulo: '',
    subtitulo: '',
    dolar_guarani: '',
    real_guarani: '',
    dolar_real: '',
    peso_guarani: '',
    placeholder: '',
    placeholder_peso: '',
    guardar: '',
    fechar: '',
    sucesso: '',
    erro: '',
    campos_obrigatorios: '',
  };

  private langChangeSub!: Subscription;

  constructor(
    private cotacaoService: CotacaoService,
    private cotacaoDialogService: CotacaoDialogService,
    private messageService: MessageService,
    private translate: TranslateService,
    private empresaService: EmpresaConfigService,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
    this.resolverMoeda();
  }

  /**
   * Reage a troca da moeda informada pelo pai. Necessario porque o pai pode
   * resolver a moeda de forma assincrona (ex.: `operar-venda` so conhece a moeda
   * do cliente depois de `GET /configuracaoEmpresa/contador`).
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['moedaEmpresa'] || changes['subtituloChave']) {
      this.loadTranslations();
    }
    if (changes['moedaEmpresa']) {
      this.resolverMoeda();
    }
  }

  /**
   * Define a moeda padrao usada pelo dialog.
   *
   * Opt-in: quando o pai informa `moedaEmpresa`, ela e usada DIRETO — sem
   * consultar a sessao (que responderia pela empresa errada no contexto do
   * contador). Sem o input, mantem o comportamento historico via sessao.
   */
  private resolverMoeda(): void {
    if (this.moedaEmpresa) {
      this.aplicarMoeda(this.moedaEmpresa as Moeda);
      return;
    }
    this.loadMoedaEmpresaDaSessao();
  }

  /**
   * Obtem a moeda padrao da empresa logada para determinar quais pares
   * aparecem no dialog. Em caso de falha, mantem trio tradicional.
   */
  private loadMoedaEmpresaDaSessao(): void {
    if (this.sessaoConsultada) {
      // Ja consultada: reaplica a moeda da sessao (o pai pode ter removido o override).
      if (this.moedaSessao || this.moedaResolvida) {
        this.aplicarMoeda(this.moedaSessao);
      }
      return;
    }
    this.sessaoConsultada = true;

    this.empresaService.getSessao().subscribe({
      next: (config: any) => {
        const enumStr = config?.moeda?.enum as string | undefined;
        this.moedaSessao = enumStr ? (enumStr as Moeda) : null;
        if (!this.moedaEmpresa) {
          this.aplicarMoeda(this.moedaSessao);
        }
      },
      error: () => {
        this.aplicarMoeda(null);
      },
    });
  }

  /** Guarda a moeda resolvida e recalcula os campos visiveis a partir dela. */
  private aplicarMoeda(moeda: Moeda | null): void {
    this.moedaResolvida = moeda;
    this.camposVisiveis = camposCotacaoVisiveis(moeda);
  }

  /** Indica se um campo de cotacao deve ser exibido/validado no dialog. */
  isVisivel(campo: CampoCotacao): boolean {
    return this.camposVisiveis.includes(campo);
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('cotacao_dialog.titulo'),
      subtitulo: this.translate.instant(this.subtituloChave || 'cotacao_dialog.subtitulo'),
      dolar_guarani: this.translate.instant('cotacao_dialog.dolar_guarani'),
      real_guarani: this.translate.instant('cotacao_dialog.real_guarani'),
      dolar_real: this.translate.instant('cotacao_dialog.dolar_real'),
      peso_guarani: this.translate.instant('cotacao_dialog.peso_guarani'),
      placeholder: this.translate.instant('cotacao_dialog.placeholder'),
      placeholder_peso: this.translate.instant('cotacao_dialog.placeholder_peso'),
      guardar: this.translate.instant('cotacao_dialog.guardar'),
      fechar: this.translate.instant('cotacao_dialog.fechar'),
      sucesso: this.translate.instant('cotacao_dialog.sucesso'),
      erro: this.translate.instant('cotacao_dialog.erro'),
      campos_obrigatorios: this.translate.instant('cotacao_dialog.campos_obrigatorios'),
    };
  }

  onShow(): void {
    this.buscarCotacaoExterna();
  }

  buscarCotacaoExterna(): void {
    this.cotacaoCarregando = true;

    fetch('https://www.cambioschaco.com.py/api/branch_office/27/exchange')
      .then(response => response.json())
      .then((data: any) => {
        const items = data.items || [];
        const usd = items.find((i: any) => i.isoCode === 'USD');
        const brl = items.find((i: any) => i.isoCode === 'BRL');
        const ars = items.find((i: any) => i.isoCode === 'ARS');

        // Pre-preenche TODOS os pares em memoria SEMPRE (mesmo quando nao
        // sao visiveis), para que qualquer moeda padrao consiga enviar o
        // payload completo exigido pelo backend (persistencia preventiva).
        this.dolarGuaraniInput = usd?.purchasePrice || 7180;
        this.realGuaraniInput = brl?.purchasePrice || 1300;
        this.dolarRealInput = brl?.purchaseArbitrage || 5.5;
        this.pesoGuaraniInput = ars?.purchasePrice || CotacaoDialogComponent.PESO_GUARANI_FALLBACK;

        this.cotacaoCarregando = false;
      })
      .catch(() => {
        this.dolarGuaraniInput = 7180;
        this.realGuaraniInput = 1300;
        this.dolarRealInput = 5.5;
        this.pesoGuaraniInput = CotacaoDialogComponent.PESO_GUARANI_FALLBACK;
        this.cotacaoCarregando = false;
      });
  }

  salvarCotacao(): void {
    // Validacao dinamica por moeda padrao da empresa.
    const trioFaltando = (this.isVisivel('dolarGuarani') && !this.dolarGuaraniInput)
      || (this.isVisivel('realGuarani') && !this.realGuaraniInput)
      || (this.isVisivel('dolarReal') && !this.dolarRealInput);
    const pesoRequiredFaltando = this.isVisivel('pesoGuarani')
      && (this.pesoGuaraniInput == null || this.pesoGuaraniInput <= 0);
    if (trioFaltando || pesoRequiredFaltando) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.texts.campos_obrigatorios,
      });
      return;
    }

    const body: any = {
      moedaPadrao: (this.moedaResolvida ?? 'GUARANI'),
      emissao: toLocalISOString(new Date()),
      // Persistencia preventiva: TODOS os pares sao sempre enviados ao
      // backend, com fallback = 1 quando o campo nao e visivel ou nao foi
      // preenchido. Inclui pesoGuarani mesmo em empresas REAL/DOLAR (ocultas)
      // para evitar regressao caso a empresa migre para GUARANI/PESO.
      dolarGuarani: this.resolverCampoObrigatorio(this.dolarGuaraniInput),
      realGuarani:  this.resolverCampoObrigatorio(this.realGuaraniInput),
      dolarReal:    this.resolverCampoObrigatorio(this.dolarRealInput),
      pesoGuarani:  this.resolverCampoObrigatorio(this.pesoGuaraniInput),
    };

    if (this.empresaId) {
      body.empresaId = this.empresaId;
    }

    this.cotacaoService.post('/cotacao', body).subscribe({
      next: (cotacaoSalva) => {
        // Cotacao nova: descarta o cache para que a proxima consulta a enxergue.
        this.cotacaoService.clearCotacaoCache();
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.texts.sucesso,
        });

        const valores: CotacaoSalvaPayload = {
          dolarGuarani: this.dolarGuaraniInput,
          realGuarani: this.realGuaraniInput,
          dolarReal: this.dolarRealInput,
          pesoGuarani: this.pesoGuaraniInput,
          cotacao: cotacaoSalva ?? null,
        };

        this.cotacaoSalva.emit(valores);
        this.cotacaoDialogService.notificarCotacaoSalva(valores);

        this.fechar();
      },
      error: (err) => {
        const codigo: string | undefined = err?.error?.code;
        const mensagemBackend: string | undefined = err?.error?.message;
        const chave = codigo ? `erros.${codigo}` : '';
        const traduzida = chave ? this.translate.instant(chave) : '';
        const detalhe = (traduzida && traduzida !== chave)
          ? traduzida
          : (mensagemBackend || this.texts.erro);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: detalhe,
        });
      },
    });
  }

  /**
   * Resolve o valor de um campo tradicional (dolarGuarani, realGuarani,
   * dolarReal) para envio obrigatorio ao backend — mesmo quando a moeda
   * padrao da empresa nao exibe o campo (ex.: PESO so ve pesoGuarani).
   *
   * Para empresas PESO o fallback e o valor pre-carregado pelo cambioschaco
   * em buscarCotacaoExterna() (quando ha rede); senao usa 1.
   */
  private resolverCampoObrigatorio(valor: number | null | undefined): number {
    if (valor != null && !Number.isNaN(valor) && valor > 0) {
      return valor;
    }
    return 1;
  }

  /** Botao "Fechar": fecha sem salvar e avisa quem estiver aguardando a cotacao. */
  cancelar(): void {
    this.fechar();
    this.cancelado.emit();
    this.cotacaoDialogService.notificarCotacaoCancelada();
  }

  fechar(): void {
    this.visivel = false;
    this.visivelChange.emit(false);
  }
}
